import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, Pin, Users, Activity, Send, Reply, 
  MoreVertical, Trash2, Edit2, Check, X, Eye, Smile,
  ChevronDown, ChevronUp, Circle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import * as api from '@/services/api';

const EMOJI_REACTIONS = ['👍', '👎', '❤️', '🔥', '👀', '🚀'];

const priorityColors = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
};

// Comment Item Component
function CommentItem({ comment, currentUser, onReply, onDelete, onResolve, onReaction }) {
  const [showReactions, setShowReactions] = useState(false);
  const isAuthor = comment.author_id === currentUser?.id;

  return (
    <div className={`p-3 rounded-lg ${comment.is_resolved ? 'bg-green-900/20 border border-green-700/30' : 'bg-slate-700/30'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center text-white text-sm font-medium">
            {comment.author_name?.charAt(0).toUpperCase()}
          </div>
          <span className="text-white font-medium text-sm">{comment.author_name}</span>
          {comment.author_role && (
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
              {comment.author_role}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {comment.is_resolved && (
            <Badge className="bg-green-500/20 text-green-400">
              <Check className="h-3 w-3 mr-1" />Resolved
            </Badge>
          )}
          <span className="text-xs text-slate-500">
            {new Date(comment.created_at).toLocaleString()}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreVertical className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {!comment.is_resolved && (
                <DropdownMenuItem onClick={() => onResolve(comment.id)}>
                  <Check className="h-4 w-4 mr-2" />Mark Resolved
                </DropdownMenuItem>
              )}
              {isAuthor && (
                <DropdownMenuItem onClick={() => onDelete(comment.id)} className="text-red-400">
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <p className="text-slate-200 text-sm whitespace-pre-wrap">{comment.content}</p>
      <div className="flex items-center gap-4 mt-3 pt-2 border-t border-slate-700/50">
        <div className="flex items-center gap-1">
          {Object.entries(comment.reactions || {}).map(([emoji, users]) => (
            <button
              key={emoji}
              onClick={() => onReaction(comment.id, emoji)}
              className={`px-2 py-0.5 rounded-full text-sm flex items-center gap-1 ${
                users?.includes(currentUser?.id) ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-slate-700/50'
              }`}
            >
              {emoji}<span className="text-xs text-slate-400">{users?.length || 0}</span>
            </button>
          ))}
          <div className="relative">
            <button onClick={() => setShowReactions(!showReactions)} className="p-1 rounded hover:bg-slate-600/50">
              <Smile className="h-4 w-4 text-slate-400" />
            </button>
            {showReactions && (
              <div className="absolute bottom-full left-0 bg-slate-700 border border-slate-600 rounded-lg p-2 flex gap-1 mb-1 shadow-lg z-10">
                {EMOJI_REACTIONS.map(emoji => (
                  <button key={emoji} onClick={() => { onReaction(comment.id, emoji); setShowReactions(false); }}
                    className="hover:bg-slate-600 p-1 rounded text-lg">{emoji}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={onReply} className="text-sm text-slate-400 hover:text-cyan-400 flex items-center gap-1">
          <Reply className="h-4 w-4" />Reply
        </button>
      </div>
    </div>
  );
}

// Main Collaboration Panel
export default function CollaborationPanel({ entityType, entityId, apiClient, currentUser, token, isOpen }) {
  const [activeTab, setActiveTab] = useState('comments');
  const [comments, setComments] = useState([]);
  const [pins, setPins] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [newPin, setNewPin] = useState({ title: '', description: '', priority: 'medium', evidence_type: 'note' });
  const [isConnected, setIsConnected] = useState(false);
  const [stats, setStats] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [commentsRes, pinsRes, usersRes, activitiesRes, statsRes] = await Promise.all([
        api.getComments(apiClient, entityType, entityId),
        api.getEvidencePins(apiClient, entityType, entityId),
        api.getActiveUsers(apiClient, entityType, entityId),
        api.getEntityActivities(apiClient, entityType, entityId),
        api.getCollaborationStats(apiClient, entityType, entityId)
      ]);
      setComments(commentsRes.data.comments || []);
      setPins(pinsRes.data.pins || []);
      setActiveUsers(usersRes.data.active_users || []);
      setActivities(activitiesRes.data.activities || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load collaboration data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiClient, entityType, entityId]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      api.joinEntityPresence(apiClient, entityType, entityId, 'viewing').catch(() => {});
    }
    return () => {
      api.leaveEntityPresence(apiClient, entityType, entityId).catch(() => {});
    };
  }, [isOpen, loadData, apiClient, entityType, entityId]);

  // WebSocket connection
  useEffect(() => {
    if (!token || !entityType || !entityId || !isOpen) return;
    const wsUrl = api.getCollaborationWSUrl(entityType, entityId, token);
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => setIsConnected(true);
    ws.onclose = () => setIsConnected(false);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'comment_new') setComments(prev => [...prev, data.comment]);
      else if (data.type === 'comment_delete') setComments(prev => prev.filter(c => c.id !== data.comment_id));
      else if (data.type === 'pin_new') setPins(prev => [...prev, data.pin]);
      else if (data.type === 'pin_delete') setPins(prev => prev.filter(p => p.id !== data.pin_id));
      else if (data.type === 'presence_join') {
        setActiveUsers(prev => prev.find(u => u.user_id === data.user_id) ? prev : [...prev, { user_id: data.user_id, user_name: data.user_name }]);
      }
      else if (data.type === 'presence_leave') setActiveUsers(prev => prev.filter(u => u.user_id !== data.user_id));
      else if (data.type === 'presence_list') setActiveUsers(data.users || []);
    };
    return () => { if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [token, entityType, entityId, isOpen]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.createComment(apiClient, {
        entity_type: entityType,
        entity_id: entityId,
        content: newComment,
        parent_comment_id: replyingTo?.id || null,
        mentions: []
      });
      setNewComment('');
      setReplyingTo(null);
      if (!isConnected) loadData();
    } catch (error) {
      console.error('Failed to submit comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.deleteComment(apiClient, commentId);
      if (!isConnected) loadData();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      await api.updateComment(apiClient, commentId, { is_resolved: true });
      loadData();
    } catch (error) {
      console.error('Failed to resolve comment:', error);
    }
  };

  const handleToggleReaction = async (commentId, emoji) => {
    const comment = comments.find(c => c.id === commentId);
    const hasReacted = comment?.reactions?.[emoji]?.includes(currentUser?.id);
    try {
      if (hasReacted) await api.removeCommentReaction(apiClient, commentId, emoji);
      else await api.addCommentReaction(apiClient, commentId, emoji);
      loadData();
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    }
  };

  const handleCreatePin = async () => {
    if (!newPin.title.trim()) return;
    try {
      await api.createEvidencePin(apiClient, { entity_type: entityType, entity_id: entityId, ...newPin });
      setShowPinDialog(false);
      setNewPin({ title: '', description: '', priority: 'medium', evidence_type: 'note' });
      if (!isConnected) loadData();
    } catch (error) {
      console.error('Failed to create pin:', error);
    }
  };

  const handleDeletePin = async (pinId) => {
    try {
      await api.deleteEvidencePin(apiClient, pinId);
      if (!isConnected) loadData();
    } catch (error) {
      console.error('Failed to delete pin:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden" data-testid="collaboration-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-white">Collaboration</h3>
          <div className={`flex items-center gap-1.5 text-xs ${isConnected ? 'text-green-400' : 'text-slate-400'}`}>
            <Circle className={`h-2 w-2 ${isConnected ? 'fill-green-400' : 'fill-slate-400'}`} />
            {isConnected ? 'Live' : 'Offline'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {activeUsers.slice(0, 5).map((user) => (
              <div key={user.user_id} className="w-8 h-8 rounded-full bg-cyan-600 border-2 border-slate-800 flex items-center justify-center text-xs font-medium text-white" title={user.user_name}>
                {user.user_name?.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
          <span className="text-xs text-slate-400">{activeUsers.length} viewing</span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-4 bg-slate-800/50 rounded-none border-b border-slate-700">
          <TabsTrigger value="comments" className="data-[state=active]:bg-slate-700">
            <MessageSquare className="h-4 w-4 mr-2" />Comments
            {stats?.comments_count > 0 && <Badge variant="secondary" className="ml-2 bg-cyan-500/20 text-cyan-400">{stats.comments_count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="pins" className="data-[state=active]:bg-slate-700">
            <Pin className="h-4 w-4 mr-2" />Evidence
            {stats?.pins_count > 0 && <Badge variant="secondary" className="ml-2 bg-yellow-500/20 text-yellow-400">{stats.pins_count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-slate-700">
            <Activity className="h-4 w-4 mr-2" />Activity
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
            <Users className="h-4 w-4 mr-2" />Team
          </TabsTrigger>
        </TabsList>

        {/* Comments Tab */}
        <TabsContent value="comments" className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No comments yet. Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.filter(c => !c.parent_comment_id).map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUser={currentUser}
                  onReply={() => setReplyingTo(comment)}
                  onDelete={handleDeleteComment}
                  onResolve={handleResolveComment}
                  onReaction={handleToggleReaction}
                />
              ))}
            </div>
          )}
          <div className="border-t border-slate-700 pt-4 mt-4">
            {replyingTo && (
              <div className="flex items-center gap-2 mb-2 text-sm text-slate-400 bg-slate-700/50 p-2 rounded">
                <Reply className="h-4 w-4" />Replying to {replyingTo.author_name}
                <Button variant="ghost" size="sm" onClick={() => setReplyingTo(null)}><X className="h-3 w-3" /></Button>
              </div>
            )}
            <div className="relative">
              <Textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white pr-20 min-h-[80px]"
                data-testid="comment-input"
              />
              <Button className="absolute bottom-2 right-2 bg-cyan-600 hover:bg-cyan-700" size="sm" onClick={handleSubmitComment} disabled={!newComment.trim()} data-testid="submit-comment-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Evidence Pins Tab */}
        <TabsContent value="pins" className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
          <div className="flex justify-between items-center">
            <h4 className="font-medium text-white">Pinned Evidence</h4>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setShowPinDialog(true)} data-testid="add-pin-btn">
              <Pin className="h-4 w-4 mr-2" />Pin Evidence
            </Button>
          </div>
          {pins.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Pin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No pinned evidence. Pin important items for the team.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pins.map(pin => (
                <div key={pin.id} className={`p-3 rounded-lg border ${priorityColors[pin.priority]}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Pin className="h-4 w-4" />
                        <h5 className="font-medium text-white">{pin.title}</h5>
                        <Badge variant="outline" className={priorityColors[pin.priority]}>{pin.priority}</Badge>
                      </div>
                      {pin.description && <p className="text-sm text-slate-300 mt-1">{pin.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>Pinned by {pin.pinned_by_name}</span>
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{pin.view_count} views</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeletePin(pin.id)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="p-4 max-h-[400px] overflow-y-auto">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No activity yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map(activity => (
                <div key={activity.id} className="flex items-start gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Activity className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white">
                      <span className="font-medium">{activity.actor_name}</span>{' '}
                      <span className="text-slate-400">{activity.activity_type.replace(/_/g, ' ')}</span>
                    </p>
                    <p className="text-xs text-slate-500">{new Date(activity.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="users" className="p-4 max-h-[400px] overflow-y-auto">
          <h4 className="font-medium text-white mb-4">Currently Viewing ({activeUsers.length})</h4>
          {activeUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No one else is here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeUsers.map(user => (
                <div key={user.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-700/30">
                  <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center text-white font-medium">
                    {user.user_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{user.user_name}</p>
                    <p className="text-xs text-slate-400">{user.user_role}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Circle className="h-2 w-2 fill-green-400 text-green-400" />
                    <span className="text-xs text-green-400">Active</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pin Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Pin Evidence</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Title</label>
              <Input value={newPin.title} onChange={(e) => setNewPin({ ...newPin, title: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Evidence title" data-testid="pin-title-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description</label>
              <Textarea value={newPin.description} onChange={(e) => setNewPin({ ...newPin, description: e.target.value })} className="bg-slate-700 border-slate-600 text-white" placeholder="Add details..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Priority</label>
                <Select value={newPin.priority} onValueChange={(v) => setNewPin({ ...newPin, priority: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Type</label>
                <Select value={newPin.evidence_type} onValueChange={(v) => setNewPin({ ...newPin, evidence_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="link">Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinDialog(false)}>Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={handleCreatePin} data-testid="save-pin-btn"><Pin className="h-4 w-4 mr-2" />Pin Evidence</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
